import React, { useEffect, useState } from 'react';
import { userAPI } from '../lib/api';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { 
  Trophy, 
  Medal,
  Crown,
  Star
} from 'lucide-react';
import { generateInitials, getEloRank } from '../lib/utils';

export default function Leaderboard() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        const response = await userAPI.getLeaderboard(50);
        setUsers(response.data || []);
      } catch (error) {
        console.error('Failed to fetch leaderboard:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLeaderboard();
  }, []);

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Crown className="w-6 h-6 text-yellow-500" />;
      case 2:
        return <Medal className="w-6 h-6 text-slate-400" />;
      case 3:
        return <Medal className="w-6 h-6 text-amber-600" />;
      default:
        return <span className="w-6 h-6 flex items-center justify-center font-bold text-muted-foreground">{rank}</span>;
    }
  };

  const getRankBg = (rank) => {
    switch (rank) {
      case 1:
        return 'bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200';
      case 2:
        return 'bg-gradient-to-r from-slate-50 to-slate-100 border-slate-200';
      case 3:
        return 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200';
      default:
        return 'bg-slate-50 border-slate-100';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-8 w-48 mb-8" />
          <div className="space-y-3">
            {[...Array(10)].map((_, i) => (
              <Skeleton key={i} className="h-20 rounded-xl" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="leaderboard-page">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-2xl mb-4 shadow-lg">
            <Trophy className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-display font-bold text-2xl sm:text-3xl text-tunis-blue">
            Rangliste
          </h1>
          <p className="text-muted-foreground mt-2">Die besten Spieler in Tunis</p>
        </div>

        {/* Top 3 Podium */}
        {users.length >= 3 && (
          <div className="flex items-end justify-center gap-4 mb-8">
            {/* 2nd Place */}
            <div className="flex flex-col items-center">
              <Avatar className="w-16 h-16 border-4 border-slate-300 shadow-lg mb-2">
                <AvatarImage src={users[1]?.avatar_url} />
                <AvatarFallback className="bg-slate-500 text-white text-lg font-bold">
                  {generateInitials(users[1]?.name || 'P')}
                </AvatarFallback>
              </Avatar>
              <Medal className="w-8 h-8 text-slate-400 -mt-4 bg-white rounded-full p-1" />
              <p className="font-medium text-sm mt-2 text-center truncate max-w-20">{users[1]?.name}</p>
              <p className="font-bold text-tunis-blue">{users[1]?.elo_points}</p>
              <div className="w-20 h-16 bg-gradient-to-t from-slate-200 to-slate-100 rounded-t-lg" />
            </div>

            {/* 1st Place */}
            <div className="flex flex-col items-center">
              <Avatar className="w-20 h-20 border-4 border-yellow-400 shadow-lg mb-2">
                <AvatarImage src={users[0]?.avatar_url} />
                <AvatarFallback className="bg-yellow-500 text-white text-xl font-bold">
                  {generateInitials(users[0]?.name || 'P')}
                </AvatarFallback>
              </Avatar>
              <Crown className="w-8 h-8 text-yellow-500 -mt-4 bg-white rounded-full p-1" />
              <p className="font-medium text-sm mt-2 text-center truncate max-w-24">{users[0]?.name}</p>
              <p className="font-bold text-lg text-tunis-blue">{users[0]?.elo_points}</p>
              <div className="w-24 h-24 bg-gradient-to-t from-yellow-200 to-yellow-100 rounded-t-lg" />
            </div>

            {/* 3rd Place */}
            <div className="flex flex-col items-center">
              <Avatar className="w-16 h-16 border-4 border-amber-400 shadow-lg mb-2">
                <AvatarImage src={users[2]?.avatar_url} />
                <AvatarFallback className="bg-amber-500 text-white text-lg font-bold">
                  {generateInitials(users[2]?.name || 'P')}
                </AvatarFallback>
              </Avatar>
              <Medal className="w-8 h-8 text-amber-600 -mt-4 bg-white rounded-full p-1" />
              <p className="font-medium text-sm mt-2 text-center truncate max-w-20">{users[2]?.name}</p>
              <p className="font-bold text-tunis-blue">{users[2]?.elo_points}</p>
              <div className="w-20 h-12 bg-gradient-to-t from-amber-200 to-amber-100 rounded-t-lg" />
            </div>
          </div>
        )}

        {/* Full List */}
        <Card className="border-slate-200" data-testid="leaderboard-list">
          <CardContent className="p-4">
            <div className="space-y-2">
              {users.map((player, index) => {
                const rank = index + 1;
                const eloRank = getEloRank(player.elo_points);
                
                return (
                  <div
                    key={player.id}
                    className={`flex items-center gap-4 p-4 rounded-xl border ${getRankBg(rank)} transition-all hover:shadow-sm`}
                    data-testid={`leaderboard-rank-${rank}`}
                  >
                    {/* Rank */}
                    <div className="w-8 flex justify-center">
                      {getRankIcon(rank)}
                    </div>

                    {/* Avatar */}
                    <Avatar className={`h-12 w-12 border-2 ${rank <= 3 ? 'border-amber-300' : 'border-slate-200'}`}>
                      <AvatarImage src={player.avatar_url} />
                      <AvatarFallback className="bg-tunis-blue text-white font-bold">
                        {generateInitials(player.name)}
                      </AvatarFallback>
                    </Avatar>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-tunis-blue truncate">{player.name}</span>
                        <Badge className={`${eloRank.bg} ${eloRank.color} text-xs shrink-0`}>
                          {eloRank.name}
                        </Badge>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {player.matches_played} Spiele • {player.wins} Siege
                      </div>
                    </div>

                    {/* ELO */}
                    <div className="text-right shrink-0">
                      <div className="flex items-center gap-1">
                        <Trophy className="w-4 h-4 text-amber-500" />
                        <span className="font-bold text-lg text-tunis-blue">{player.elo_points}</span>
                      </div>
                      <div className="text-xs text-muted-foreground">ELO</div>
                    </div>
                  </div>
                );
              })}

              {users.length === 0 && (
                <div className="text-center py-12">
                  <Trophy className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-muted-foreground">Noch keine Spieler in der Rangliste</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
